/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */

import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Box, IconButton } from '@material-ui/core';
import { differenceInSeconds } from 'date-fns';
import { CancelRounded } from '@material-ui/icons';
import { optimoveEvents } from 'src/screens/optimove';
import useUserInfo from 'src/hooks/useUserInfo';
import { saveDigitalCoupon } from 'src/lib/api-client/wallet-activities';
import useEventsReporter from 'src/hooks/useEventsReporter';
import eventMetricsMap from 'src/hooks/useEventsReporter/event-types';
import useCardCanvas from 'src/hooks/useCardCanvas';
import { makeSearchString } from 'src/lib/utils/url-utils';
import Copy from 'src/assets/icons/copy.svg';
import { useUserUpdateContext } from 'src/lib/contexts/user-update-context';
import { Body, Button, Pill } from '../UI';
import { IconSmallCheckmark } from '../Icons';
import useStyles from './styles';
import CommonModal from '../CommonModal';
import Row from '../Row';

function CouponSaveButton({
    expirationDate,
    rewardsNumber,
    couponNumber,
    descriptionText1,
    descriptionText2,
    setSaveCouponCall,
    setSaveCouponFetch,
    setIsOpenUnclipModal,
    setModalText,
}: {
    expirationDate: string;
    rewardsNumber?: string;
    couponNumber: number;
    descriptionText1: string;
    descriptionText2: string;
    setSaveCouponCall: (flag: boolean) => void;
    setSaveCouponFetch: (flag: boolean) => void;
    setIsOpenUnclipModal: React.Dispatch<React.SetStateAction<boolean>>;
    setModalText: React.Dispatch<React.SetStateAction<string>>;
}) {
    const classes = useStyles();
    const { customEvent, analyticsCustomEvent, analyticsScreenCustomNames } =
        useEventsReporter();
    const { userInfo } = useUserInfo();
    const saveDigitalCouponQuery = async (e: any) => {
        e.preventDefault();
        setSaveCouponFetch(true);
        if (rewardsNumber) {
            const offerdescription = `${descriptionText1} ${descriptionText2}`;
            const parameter = {
                enrolled: rewardsNumber !== '0',
                coupon_id: couponNumber,
                rewards_number: rewardsNumber,
                offer_title: `${descriptionText1} ${descriptionText2}`,
            };
            customEvent(eventMetricsMap.save_reward, parameter);
            optimoveEvents(eventMetricsMap.scm_wallet, {
                click_text: 'save & extend reward',
                element_location: 'available',
                wallet_location: 'digital coupons',
                offer_description: `${descriptionText1} ${descriptionText2}`,
            });
            analyticsCustomEvent(eventMetricsMap.easy_rewards, {
                click_text: 'clip coupon',
                element_location: 'coupon view',
                offer_description: offerdescription?.substring(0, 50),
                coupon_number: couponNumber,
            });
            try {
                const saveCouponData = await saveDigitalCoupon({
                    rewardsNumber,
                    couponCode: couponNumber,
                    isSaved: true,
                });
                if (saveCouponData?.data?.success) {
                    setSaveCouponCall(true);
                    setIsOpenUnclipModal(false);
                } else {
                    setSaveCouponFetch(false);
                    setIsOpenUnclipModal(true);
                }
            } catch (error) {
                setModalText('Unable to clip coupon');
                setIsOpenUnclipModal(true);
                setSaveCouponFetch(false);
            }
        }
        return null;
    };

    useEffect(() => {
        if (userInfo?.rewardsNumber) {
            analyticsScreenCustomNames({
                rewards_number: userInfo?.rewardsNumber,
            });
        } else {
            analyticsScreenCustomNames({
                rewards_number: '',
            });
        }
        if (userInfo?.preferredStoreId) {
            analyticsScreenCustomNames({
                store_id: userInfo?.preferredStoreId,
            });
        } else {
            analyticsScreenCustomNames({
                store_id: '',
            });
        }
    }, []);

    return (
        <Button
            fontSize={16}
            onClick={(e) => saveDigitalCouponQuery(e)}
            variant="contained-rounded"
            bgColor="bgRed"
            className={classes.clipBtnAlign}
        >
            Clip coupon
        </Button>
    );
}

function CouponContent({
    couponApplicable,
    imageURL,
    descriptionText1,
    descriptionText2,
    couponClass,
    expirationDate,
}: {
    couponApplicable: string;
    imageURL?: string;
    descriptionText1: string;
    descriptionText2: string;
    couponClass?: string;
    expirationDate: string;
}) {
    const classes = useStyles();
    const BadgeComponent = useCallback(() => {
        const currentDateObj = new Date();
        const expirationDateObj = new Date(expirationDate);

        const differenceInDate = differenceInSeconds(
            expirationDateObj,
            currentDateObj
        );

        if (differenceInDate <= 86_400) {
            return <Pill className={classes.couponPill}>EXPIRES TODAY</Pill>;
        }
        if (differenceInDate <= 172_800) {
            return <Pill className={classes.couponPill}>2 DAYS LEFT</Pill>;
        }
        if (differenceInDate <= 259_200) {
            return <Pill className={classes.couponPill}>3 DAYS LEFT</Pill>;
        }
        if (differenceInDate <= 604_800) {
            return <Pill className={classes.couponPill}>THIS WEEK ONLY</Pill>;
        }
        return null;
    }, [expirationDate]);
    return (
        <Row
            direction="column"
            gap="12px"
            sx={{
                padding: '1rem',
            }}
        >
            <>
                {(couponApplicable === 'Product' ||
                    couponClass === 'StoreCredit') &&
                imageURL ? (
                    <Row justifyContent={'center'}>
                        <img
                            height={180}
                            width={180}
                            src={imageURL}
                            alt="Coupon"
                        />
                    </Row>
                ) : (
                    <Row justifyContent="flex-start">
                        <Body size={14}>Just for you</Body>
                    </Row>
                )}
                <Box sx={{ width: '100%' }}>
                    <BadgeComponent />
                    <Body
                        size={36}
                        weight="bold"
                        color="$red"
                        className={
                            couponApplicable !== 'Product'
                                ? classes.promoText1Primary
                                : classes.promoText1Secondary
                        }
                    >
                        {descriptionText1}
                    </Body>
                    <Body
                        size={16}
                        weight="light"
                        color="$black"
                        textAlign="textLeft"
                        lineHeight={20}
                    >
                        {descriptionText2}
                    </Body>
                </Box>
            </>
        </Row>
    );
}

type ScannableCouponProps = {
    rewardsNumber?: string;
    couponApplicable: string;
    couponNumber: number;
    availableUntil: Date;
    expirationDate: Date;
    descriptionText1: string;
    descriptionText2: string;
    imageURL?: string;
    isSaved: boolean;
    validForDotcom: boolean;
    validForRetail: boolean;
    terms: string;
    isCouponNumberDisplayed: boolean;
    couponLimitedTime: boolean;
    setSaveCouponCall: (flag: boolean) => void;
    setSaveCouponFetch: (flag: boolean) => void;
    couponClass?: string;
    isClippable?: boolean;
};

function ScannableCouponNew({
    rewardsNumber,
    couponApplicable,
    couponNumber,
    availableUntil,
    expirationDate,
    descriptionText1,
    descriptionText2,
    imageURL,
    isSaved,
    validForDotcom,
    validForRetail,
    terms,
    isCouponNumberDisplayed,
    couponLimitedTime,
    setSaveCouponCall,
    setSaveCouponFetch,
    couponClass,
    isClippable,
}: ScannableCouponProps) {
    const classes = useStyles();
    const { canvasRef } = useCardCanvas(`${couponNumber}`);
    const [copied, setCopied] = useState<boolean>(false);
    const [isOpenUnclipModal, setIsOpenUnclipModal] = useState(false);
    const [modalText, setModalText] = useState<string>('Unable to clip coupon');
    const { analyticsCustomEvent } = useEventsReporter();
    const { setSelectedCoupon, selectedCoupon } = useUserUpdateContext();

    const expirationDateObj = new Date(expirationDate);
    const formattedExpirationDate = `${
        expirationDateObj.getMonth() + 1
    }/${expirationDateObj.getDate()}/${expirationDateObj
        .getFullYear()
        .toString()
        .slice(2)}`;

    const availableUntilObj = new Date(availableUntil);
    const formattedAvailableUntil = `${
        availableUntilObj.getMonth() + 1
    }/${availableUntilObj.getDate()}/${availableUntilObj
        .getFullYear()
        .toString()
        .slice(2)}`;
    const handleUnclipped = async (e: any) => {
        e.preventDefault();
        setSaveCouponFetch(true);
        const offerdescription = `${descriptionText1} ${descriptionText2}`;

        if (rewardsNumber) {
            try {
                const saveCouponData = await saveDigitalCoupon({
                    rewardsNumber,
                    couponCode: couponNumber,
                    isSaved: false,
                });
                if (saveCouponData?.data?.success) {
                    setSaveCouponCall(true);
                    analyticsCustomEvent(eventMetricsMap.easy_rewards, {
                        click_text: 'unclip coupon',
                        element_location: 'coupon view',
                        offer_description: offerdescription?.substring(0, 50),
                        coupon_number: couponNumber,
                    });
                }
                setIsOpenUnclipModal(false);
            } catch (error) {
                setModalText('Unable to unclip coupon');
                setIsOpenUnclipModal(true);
                setSaveCouponFetch(false);
            }
        }
        return null;
    };
    let redeemText = 'Redeem this coupon';
    if (validForDotcom && validForRetail) {
        redeemText = 'In store and online';
    } else if (validForDotcom) {
        redeemText = 'Online only';
    } else if (validForRetail) {
        redeemText = 'In store only';
    }
    return (
        <>
            <div className={classes.couponContainerNew}>
                <div className={classes.promoContainer}>
                    <CouponContent
                        couponApplicable={couponApplicable}
                        imageURL={imageURL}
                        descriptionText1={descriptionText1}
                        descriptionText2={descriptionText2}
                        couponClass={couponClass}
                        expirationDate={expirationDate as unknown as string}
                    />
                </div>
                <div className={classes.barcodeAndTCContainer}>
                    <div className={classes.barcodeContainer}>
                        <div className={classes.expirationDateContainer}>
                            <Body color="textBlack" size={14}>
                                {`Exp ${formattedExpirationDate}`}
                            </Body>
                            <Body size={14} weight="light">
                                {redeemText}
                            </Body>
                        </div>

                        {isClippable ? (
                            isSaved ? (
                                <div className={classes.clipContainer}>
                                    <Button
                                        onClick={handleUnclipped}
                                        fontSize={14}
                                        fontWeight="normal"
                                        bgColor="bgTransparent"
                                        color="textBlack"
                                        className={classes.unclipText}
                                    >
                                        Unclip coupon
                                    </Button>
                                    <div className={classes.savedMessage}>
                                        <IconSmallCheckmark bgFillColor="fillRed" />{' '}
                                        Applies at register
                                    </div>
                                </div>
                            ) : (
                                <CouponSaveButton
                                    expirationDate={formattedExpirationDate}
                                    couponNumber={couponNumber}
                                    rewardsNumber={rewardsNumber}
                                    descriptionText1={descriptionText1}
                                    descriptionText2={descriptionText2}
                                    setSaveCouponCall={setSaveCouponCall}
                                    setSaveCouponFetch={setSaveCouponFetch}
                                    setIsOpenUnclipModal={setIsOpenUnclipModal}
                                    setModalText={setModalText}
                                />
                            )
                        ) : null}
                        {!selectedCoupon.includes(couponNumber) ? (
                            <Button
                                className={classes.showBarcodeButton}
                                onClick={() => {
                                    setSelectedCoupon([
                                        ...selectedCoupon,
                                        couponNumber,
                                    ]);
                                }}
                                variant="text"
                                color="textLinkBlue"
                                fontSize={14}
                            >
                                Show coupon code
                            </Button>
                        ) : (
                            <>
                                <canvas
                                    ref={canvasRef}
                                    className={classes.renderedCardNumber}
                                />
                                {isCouponNumberDisplayed && (
                                    <Box>
                                        <Box
                                            className={
                                                classes.couponNumberContainer
                                            }
                                        >
                                            <span
                                                className={classes.couponNumber}
                                            >
                                                {couponNumber}
                                            </span>

                                            <Box
                                                onClick={() => {
                                                    const offerdescription = `${descriptionText1} ${descriptionText2}`;

                                                    setCopied(true);
                                                    navigator?.clipboard?.writeText(
                                                        `${couponNumber}`
                                                    );
                                                    setTimeout(
                                                        () => setCopied(false),
                                                        1500
                                                    );
                                                    analyticsCustomEvent(
                                                        eventMetricsMap.easy_rewards,
                                                        {
                                                            click_text:
                                                                'copy coupon code',
                                                            element_location:
                                                                'coupon view',
                                                            offer_description:
                                                                offerdescription?.substring(
                                                                    0,
                                                                    50
                                                                ),
                                                            coupon_number:
                                                                couponNumber,
                                                        }
                                                    );
                                                }}
                                                sx={{
                                                    position: 'relative',
                                                    mt: '3px',
                                                }}
                                            >
                                                <img
                                                    src={Copy}
                                                    alt="Copy-Coupon-Code"
                                                />

                                                {copied && (
                                                    <span
                                                        className={
                                                            classes.infoCopied
                                                        }
                                                    >
                                                        Copied!
                                                    </span>
                                                )}
                                            </Box>
                                        </Box>

                                        <Body
                                            color="textMediumGray"
                                            size={10}
                                            textAlign="textCenter"
                                            weight="light"
                                        >
                                            Copy coupon code to redeem.
                                        </Body>
                                    </Box>
                                )}
                            </>
                        )}
                    </div>
                    <Box>
                        <Body
                            size={12}
                            weight="light"
                            lineHeight={16}
                            color="textGray"
                        >
                            {terms}
                        </Body>
                    </Box>
                </div>
            </div>
            {isOpenUnclipModal && (
                <CommonModal open={isOpenUnclipModal} isNoPadding>
                    <Row
                        direction="column"
                        gap="30px"
                        sx={{
                            px: '27px',
                            py: '16px',
                            position: 'relative',
                        }}
                    >
                        <>
                            <Box>
                                <>
                                    <Box
                                        sx={{
                                            pt: '14px',
                                            pb: '5px',
                                        }}
                                    >
                                        <Body
                                            size={18}
                                            lineHeight={22}
                                            textAlign="textCenter"
                                        >
                                            {modalText}
                                        </Body>
                                    </Box>
                                    <Box>
                                        <Body
                                            size={14}
                                            lineHeight={20}
                                            weight="light"
                                            textAlign="textCenter"
                                        >
                                            We couldn’t clip the coupon at this
                                            time.
                                        </Body>
                                        <Body
                                            size={14}
                                            lineHeight={20}
                                            weight="light"
                                            textAlign="textCenter"
                                        >
                                            Please try again later.
                                        </Body>
                                    </Box>
                                </>
                            </Box>

                            <Button
                                className={classes.gotItBtn}
                                onClick={() => setIsOpenUnclipModal(false)}
                            >
                                Got it
                            </Button>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 5,
                                }}
                            >
                                <IconButton
                                    style={{
                                        color: 'inherit',
                                        padding: 0,
                                    }}
                                    onClick={() => setIsOpenUnclipModal(false)}
                                >
                                    <CancelRounded />
                                </IconButton>
                            </Box>
                        </>
                    </Row>
                </CommonModal>
            )}
        </>
    );
}

export default ScannableCouponNew;
